import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Skeleton } from "@material-ui/lab";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { notBlank } from "../util/ext";
import { equals, find, propEq } from "ramda";
import TextField from "@material-ui/core/TextField";
import { useAllModules } from "./subscriptionService";
import { identity } from "rxjs";
import { Box } from "@material-ui/core";
import { FixedSizeList } from "react-window";
import PropTypes from "prop-types";
import Typography from "@material-ui/core/Typography";
import { matchSorter } from "match-sorter";

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
export const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref,
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 60;

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemSize * itemCount;
  };
  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          style={{
            borderStyle: "solid",
            borderColor: "#F7F9FA",
            borderWidth: 2,
          }}
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

ListboxComponent.propTypes = {
  children: PropTypes.node,
};

const filterOptions = (options, { inputValue }) =>
  matchSorter(options, inputValue, {
    keys: ["name", "displayName", "id", "status"],
  });

const ModuleAutoComplete = props => {
  const {
    autoFocus = false,
    disabled = false,
    error,
    filterFunction = identity,
    helperText,
    label = "Module",
    onChange,
    onBlur,
    value,
  } = props;
  const modules = useAllModules();
  const [selectedModule, setSelectedModule] = useState(null);

  const filterFunctionCallback = useCallback(filterFunction, []);

  useEffect(() => {
    if (modules.isSuccess) {
      setSelectedModule(find(propEq("id", value), modules.data));
    }
  }, [modules.isSuccess, modules.data, filterFunctionCallback, value]);

  const handleModuleSelect = useCallback(
    (_event, module) => {
      setSelectedModule(module);
      onChange(module ? module.id : null);
    },
    [onChange],
  );

  return (
    <Fragment>
      {modules.isLoading ? (
        <Skeleton variant="text" />
      ) : (
        <Autocomplete
          disabled={disabled}
          disableListWrap
          options={modules.data}
          value={selectedModule || null}
          ListboxComponent={ListboxComponent}
          getOptionLabel={module => notBlank(module) && `${module.name}`}
          getOptionSelected={(opt, val) =>
            notBlank(val) && equals(opt.id, val.id)
          }
          filterOptions={filterOptions}
          renderOption={(option, { selected }) => (
            <Box width="100%">
              <Box fontSize="1.4rem">
                <Typography
                  noWrap
                  color={option.status === "INACTIVE" ? "error" : "inherit"}
                >
                  {option.name}
                </Typography>
              </Box>
              <Box fontSize="1.2rem">
                <Typography
                  color={option.status === "INACTIVE" ? "error" : "inherit"}
                >{`(${option.id})`}</Typography>
              </Box>
            </Box>
          )}
          onChange={handleModuleSelect}
          onBlur={onBlur}
          renderInput={params => {
            params.inputProps["aria-autocomplete"] = "none";
            params.inputProps["id"] = null;
            return (
              <TextField
                {...params}
                autoFocus={autoFocus}
                type="text"
                name={"program-organization-" + Math.random()}
                label={label}
                error={error}
                helperText={helperText}
                fullWidth
              />
            );
          }}
        />
      )}
    </Fragment>
  );
};

ModuleAutoComplete.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  filterFunction: PropTypes.func,
  autoFocus: PropTypes.bool,
  helperText: PropTypes.string,
  error: PropTypes.bool,
};

export default ModuleAutoComplete;
